import React, { useState, useMemo, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';

import Sidebar from './Sidebar';
import ClockWidgetMount from './ClockWidgetMount';
import { useNotificationsNavigation } from './NotificationsWidget';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;
const miniDrawerWidth = 65; // Width when collapsed

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    width: '100%',
    overflow: 'auto',
    minHeight: '100vh',
    padding: theme.spacing(3),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(2),
      paddingTop: `calc(${theme.spacing(2)} + 64px)`, // Adjusted padding for mobile with fixed header height
    },
    ...(open && {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  })
);

function Layout() {
  const [open, setOpen] = useState(true);
  const { user, logout } = useAuth();
  useNotificationsNavigation();


  const handleDrawerToggle = useCallback(() => {
    setOpen(prevOpen => !prevOpen);
  }, []);

  // Memoize the layout content to prevent unnecessary re-renders
  const layoutContent = useMemo(() => (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Sidebar
        open={open}
        handleDrawerToggle={handleDrawerToggle}
        drawerWidth={drawerWidth}
        miniDrawerWidth={miniDrawerWidth}
        user={user}
        logout={logout}
        useExternalToggle={true}
      />
      <Main open={open}>
        <Outlet />
      </Main>
      <ClockWidgetMount />
    </Box>
  ), [open, user, logout, handleDrawerToggle]);

  return layoutContent;
}

export default Layout;
